import type { ApiErrorBody } from "@markfeed/shared";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

let currentOrgId: string | null = null;
export const setApiOrg = (id: string | null) => {
  currentOrgId = id;
};

type Query = Record<string, string | number | boolean | null | undefined>;

function qs(query?: Query) {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Same-origin API client. In dev Vite proxies /api → :4000; in production /api is reverse-proxied. */
export async function api<T>(
  path: string,
  opts: { method?: string; body?: unknown; query?: Query; signal?: AbortSignal; headers?: Record<string, string> } = {},
): Promise<T> {
  const headers: Record<string, string> = { "x-requested-with": "MarkFeed", ...opts.headers };
  if (currentOrgId) headers["x-org-id"] = currentOrgId;
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}${qs(opts.query)}`, {
      method: opts.method ?? (body ? "POST" : "GET"),
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new ApiError(0, "network", "Can't reach the server. Check your internet connection.");
  }
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as (ApiErrorBody & T) | null;
  if (!res.ok) {
    const e = json?.error;
    throw new ApiError(res.status, e?.code ?? "error", e?.message ?? `Request failed (${res.status})`, e?.details);
  }
  return json as T;
}

/** Upload with progress (XHR) — fetch can't report upload progress. */
export function uploadFile(
  path: string,
  file: File | Blob,
  opts: { fileName?: string; onProgress?: (pct: number) => void; headers?: Record<string, string>; absolute?: boolean } = {},
): Promise<{ id: string; fileName: string; mimeType: string; size: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", opts.absolute ? path : `/api${path}`);
    xhr.withCredentials = !opts.absolute;
    xhr.setRequestHeader("x-requested-with", "MarkFeed");
    if (currentOrgId && !opts.absolute) xhr.setRequestHeader("x-org-id", currentOrgId);
    for (const [k, v] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && opts.onProgress?.(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      let json: { error?: { message?: string; code?: string } } & Record<string, unknown> = {};
      try {
        json = JSON.parse(xhr.responseText);
      } catch {
        /* ignore */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(json as never);
      else reject(new ApiError(xhr.status, json.error?.code ?? "error", json.error?.message ?? "Upload failed"));
    };
    xhr.onerror = () => reject(new ApiError(0, "network", "Upload failed — check your connection"));
    const form = new FormData();
    form.append("file", file, opts.fileName ?? (file instanceof File ? file.name : "file"));
    xhr.send(form);
  });
}

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
